import { useCallback } from 'react'
import { Upload } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

interface FileUploadZoneProps {
  onFileSelect: (content: string, filename: string) => void
  accept?: string
  disabled?: boolean
  dropLabel?: string
  browseLabel?: string
  acceptsLabel?: string
}

export function FileUploadZone({
  onFileSelect,
  accept = '.json',
  disabled = false,
  dropLabel = 'Drop file here',
  browseLabel = 'or click to browse',
  acceptsLabel,
}: FileUploadZoneProps) {
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (disabled) return

      const file = e.dataTransfer.files.item(0)
      if (file) {
        const reader = new FileReader()
        reader.onload = (event) => {
          if (event.target?.result) {
            onFileSelect(event.target.result as string, file.name)
          }
        }
        reader.readAsText(file)
      }
    },
    [onFileSelect, disabled],
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.item(0)
      if (file) {
        const reader = new FileReader()
        reader.onload = (event) => {
          if (event.target?.result) {
            onFileSelect(event.target.result as string, file.name)
          }
        }
        reader.readAsText(file)
      }
    },
    [onFileSelect],
  )

  return (
    <div
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      className={disabled ? 'opacity-50 pointer-events-none' : ''}
    >
      <Card className="border-2 border-dashed hover:border-primary transition-colors">
        <CardContent className="pt-6">
          <label htmlFor="file-upload" className="cursor-pointer block">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Upload className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">{dropLabel}</p>
              <p className="text-sm text-muted-foreground mb-4">
                {browseLabel}
              </p>
              {acceptsLabel && (
                <p className="text-xs text-muted-foreground">{acceptsLabel}</p>
              )}
            </div>
            <input
              id="file-upload"
              type="file"
              accept={accept}
              onChange={handleFileChange}
              disabled={disabled}
              className="hidden"
            />
          </label>
        </CardContent>
      </Card>
    </div>
  )
}
